<template>
  <section class="dsa" aria-labelledby="dsa-title">
    <header class="dsa-head">
      <div>
        <h3 id="dsa-title" class="dsa-title">
          <i class="ph ph-plugs-connected" aria-hidden="true"></i>
          Accès API — changement de statut
        </h3>
        <p class="dsa-sub">
          Le système du livreur s'identifie avec son driver-code (ancien id, comme chez Kargo) pour modifier le statut de ses colis. Le token ci-dessous sert au format JSON natif.
        </p>
      </div>
      <router-link to="/driver-status-api-docs" class="btn btn-sm btn-outline-primary">
        <i class="ph ph-book-open me-1" aria-hidden="true"></i>
        Documentation API – Changement de statut
      </router-link>
    </header>

    <div v-if="loading && !access" class="dsa-empty">
      <span class="spinner-border spinner-border-sm me-2" role="status"></span> Chargement…
    </div>

    <template v-else-if="access">
      <div v-if="!access.enabled" class="dsa-note dsa-note--warn" role="note">
        <i class="ph ph-warning" aria-hidden="true"></i>
        L'option « Autoriser le changement de statut des colis via API » est désactivée :
        les appels de ce livreur sont refusés (API_ACCESS_DISABLED) même avec un driver-code valide.
      </div>
      <div v-else-if="!access.active" class="dsa-note dsa-note--warn" role="note">
        <i class="ph ph-warning" aria-hidden="true"></i>
        Le compte du livreur est désactivé : les appels sont refusés (ACCOUNT_INACTIVE).
      </div>

      <!-- Token shown once, right after generation -->
      <div v-if="plainToken" class="dsa-token-new" role="status">
        <strong>Nouveau driver-code — copiez-le maintenant, il ne sera plus affiché.</strong>
        <div class="dsa-token-row">
          <input class="form-control dsa-mono" :value="plainToken" readonly aria-label="driver-code API" @focus="($event.target as HTMLInputElement).select()" />
          <button type="button" class="btn btn-primary" @click="copy(plainToken)">
            <i class="ph ph-copy me-1" aria-hidden="true"></i>{{ copied ? "Copié" : "Copier" }}
          </button>
        </div>
      </div>

      <dl class="dsa-facts">
        <div>
          <dt>driver-code</dt>
          <dd v-if="access.driver_code"><span class="dsa-mono">{{ access.driver_code }}</span> <span class="text-muted">(à envoyer dans le paramètre driver-code)</span></dd>
          <dd v-else><span class="badge text-outline-secondary rounded-pill">Aucun ancien id</span></dd>
        </div>
        <div>
          <dt>Token JSON</dt>
          <dd v-if="access.has_token">
            <span class="badge text-outline-success rounded-pill">Actif</span>
            <span class="dsa-mono ms-2">sdl_••••{{ access.token_hint }}</span>
          </dd>
          <dd v-else><span class="badge text-outline-secondary rounded-pill">Aucun code</span></dd>
        </div>
        <div v-if="access.has_token">
          <dt>Créé le</dt>
          <dd>{{ formatDate(access.token_created_at) }}</dd>
        </div>
        <div v-if="access.has_token">
          <dt>Dernière utilisation</dt>
          <dd>{{ access.last_used_at ? formatDate(access.last_used_at) : "Jamais" }}</dd>
        </div>
      </dl>

      <div class="dsa-actions">
        <button type="button" class="btn btn-sm btn-primary" :disabled="busy" @click="generate">
          <span v-if="busy" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="ph ph-key me-1" aria-hidden="true"></i>
          {{ access.has_token ? "Régénérer le driver-code" : "Générer un driver-code" }}
        </button>
        <button v-if="access.has_token" type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="revoke">
          <i class="ph ph-prohibit me-1" aria-hidden="true"></i> Révoquer
        </button>
      </div>

      <div v-if="access.recent_calls.length" class="dsa-calls">
        <h4 class="dsa-calls-title">Derniers appels</h4>
        <div class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Colis</th>
                <th scope="col">Résultat</th>
                <th scope="col">Changement</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="call in access.recent_calls" :key="call.id">
                <td class="text-nowrap">{{ formatDate(call.created_at) }}</td>
                <td class="dsa-mono">{{ call.parcel_code || "—" }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="call.success ? 'text-outline-success' : 'text-outline-danger'"
                  >{{ call.success ? "OK" : call.error_code }}</span>
                  <span class="text-muted ms-1 small">{{ call.http_status }}</span>
                </td>
                <td class="small">
                  <template v-if="call.success">{{ call.old_status }} → {{ call.new_status }}</template>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import moment from "moment";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";

interface ApiCall {
  id: number;
  parcel_code: string | null;
  old_status: string | null;
  new_status: string | null;
  success: boolean;
  http_status: number;
  error_code: string | null;
  created_at: string;
}
interface Access {
  enabled: boolean;
  active: boolean;
  driver_code?: string | null;
  has_token: boolean;
  token_hint: string | null;
  token_created_at: string | null;
  last_used_at: string | null;
  recent_calls: ApiCall[];
}

export default defineComponent({
  name: "DriverStatusApiAccess",
  props: {
    userId: { type: [String, Number], required: true },
    // bumped by the parent after the user form is saved (the option may have changed)
    refreshKey: { type: Number, default: 0 },
  },
  setup(props) {
    const { showToast } = useAppToast();
    const access = ref<Access | null>(null);
    const loading = ref(false);
    const busy = ref(false);
    const plainToken = ref("");
    const copied = ref(false);

    const load = async () => {
      loading.value = true;
      try {
        const res = await api.get(`/api/auth/driver-status-api/drivers/${props.userId}`);
        access.value = res.data.access;
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Impossible de charger l'accès API.", "error");
      } finally {
        loading.value = false;
      }
    };

    const generate = async () => {
      if (access.value?.has_token) {
        const confirm = await Swal.fire({
          text: "Régénérer le driver-code ? L'ancien code cessera immédiatement de fonctionner.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Oui, régénérer",
          cancelButtonText: "Annuler",
        });
        if (!confirm.isConfirmed) return;
      }
      busy.value = true;
      try {
        const res = await api.post(`/api/auth/driver-status-api/drivers/${props.userId}/token`);
        access.value = res.data.access;
        plainToken.value = res.data.token;
        copied.value = false;
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Génération du driver-code impossible.", "error");
      } finally {
        busy.value = false;
      }
    };

    const revoke = async () => {
      const confirm = await Swal.fire({
        text: "Révoquer le driver-code ? Le système du livreur ne pourra plus appeler l'API.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, révoquer",
        cancelButtonText: "Annuler",
      });
      if (!confirm.isConfirmed) return;
      busy.value = true;
      try {
        const res = await api.post(`/api/auth/driver-status-api/drivers/${props.userId}/token/revoke`);
        access.value = res.data.access;
        plainToken.value = "";
        showToast("driver-code révoqué.", "success");
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Révocation impossible.", "error");
      } finally {
        busy.value = false;
      }
    };

    const copy = async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        copied.value = true;
      } catch {
        showToast("Copie impossible : sélectionnez le code et copiez-le manuellement.", "error");
      }
    };

    const formatDate = (value: string | null) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "—");

    onMounted(load);
    watch(() => props.refreshKey, load);

    return { access, loading, busy, plainToken, copied, generate, revoke, copy, formatDate };
  },
});
</script>

<style scoped>
.dsa {
  margin-top: var(--ui-space-2);
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface-sunken);
}
.dsa-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ui-space-3);
  margin-bottom: var(--ui-space-4);
}
.dsa-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-semibold);
  color: var(--ui-text);
}
.dsa-sub {
  margin: 0;
  font-size: var(--ui-fs-sm);
  color: var(--ui-text-muted);
}
.dsa-empty {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.dsa-note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: var(--ui-space-4);
  padding: 10px 12px;
  border-radius: var(--ui-radius-md);
  font-size: var(--ui-fs-sm);
}
.dsa-note--warn {
  background: var(--ui-warning-soft);
  color: var(--ui-text);
}
.dsa-note--warn i {
  color: var(--ui-warning-strong, var(--ui-warning));
  font-size: 18px;
}
.dsa-token-new {
  margin-bottom: var(--ui-space-4);
  padding: 12px;
  border: 1px solid var(--ui-success);
  border-radius: var(--ui-radius-md);
  background: var(--ui-success-soft);
  font-size: var(--ui-fs-sm);
}
.dsa-token-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.dsa-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
}
.dsa-facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ui-space-3) var(--ui-space-6);
  margin: 0 0 var(--ui-space-4);
}
.dsa-facts dt {
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
  color: var(--ui-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.dsa-facts dd {
  margin: 2px 0 0;
  color: var(--ui-text);
}
.dsa-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dsa-calls {
  margin-top: var(--ui-space-5);
}
.dsa-calls-title {
  margin-bottom: 8px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  color: var(--ui-text);
}
@media (max-width: 575.98px) {
  .dsa-token-row {
    flex-direction: column;
  }
}
</style>
